<template>
  <div class="lib__form__main" data-cy="temporary-meter-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('wantedConfiguration.title') }}</h2>
        </header>
        <div class="lib__feedback lib__feedback--alert" data-cy="meter-under-reserve-of-availability">
          <div class="lib__feedback__icon">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <div class="lib__feedback__content">
            <p>{{ $t('meter.underReserveOfAvailability') }}</p>
          </div>
        </div>

        <temporary-electricity-meter-forms :tensions="electricityTensions" :value="configurationInfo.electricityMeters"></temporary-electricity-meter-forms>

        <fieldset class="lib__form__fieldset">
          <legend class="lib__form__legend">{{ $t('connectionPeriod.wantedDates', [$t('common.temporaryConnectionLower')]) }}</legend>
          <p v-html="$t('connectionPeriod.priceInfo.temporary', [weeks])"></p>
          <p v-html="$t('connectionPeriod.recontact')"></p>

          <div class="row">
            <div class="col-xs-12 col-md-6">
              <DateBox :label="$t('connectionPeriod.form.startDate')" :max="endDate" :min="minDate" data-cy="meter-start-date" required v-model="configurationInfo.startDate"></DateBox>
            </div>
            <div class="col-xs-12 col-md-6">
              <DateBox :label="$t('connectionPeriod.form.endDate')" :max="maxDate" :min="minEndDate" data-cy="meter-end-date" required v-model="configurationInfo.endDate"></DateBox>
            </div>
          </div>

          <p class="lib__text-weight-600" data-cy="meter-selected-period" v-html="$t('connectionPeriod.selectedPeriod', [$t('common.temporaryConnectionLower'), selectedPeriod])" v-if="selectedPeriod !== null"></p>

          <temporary-price-calculator :configuration-info="configurationInfo"></temporary-price-calculator>
        </fieldset>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Inject, Watch } from 'vue-property-decorator'
import { helper, meterService, dateService, calendarService } from '@/services'
import Step from '@/components/steps/Step'
import TemporaryElectricityMeterForms from '@/components/inputs/Configuration/TemporaryElectricityMeterForms.vue'
import TemporaryPriceCalculator from '@/components/display/TemporaryPriceCalculator.vue'
import { State, Mutation, Getter } from 'vuex-class'
import {
  TemporaryConfigurationInfo,
  MeterElectricityType,
  MeterElectricityTension,
  TemporaryElectricityPriceRequest,
  TemporaryElectricityPriceResponse
} from '@/models'

@Component({
  name: 'configuration-info-step',
  components: {
    TemporaryElectricityMeterForms,
    TemporaryPriceCalculator,
  }
})
export default class ConfigurationInfoStep extends Step {
  //#region [Data]
  @State('requestId', { namespace: 'temporaryConnection' })
  public requestId!: string
  @State('electricityTensions', { namespace: 'temporaryConnection/configurationInfo' })
  public readonly electricityTensions!: MeterElectricityTension[]

  @State('configurationInfo', { namespace: 'temporaryConnection/configurationInfo' })
  public configurationInfoState!: TemporaryConfigurationInfo | null

  public configurationInfo: TemporaryConfigurationInfo = {
    electricityMeters: [],
    startDate: null,
    endDate: null
  }
  //#endregion

  //#region [Computed]
  public get minDate(): string {
    const now = new Date()
    const endDate =
      this.configurationInfo.endDate === null
        ? null
        : calendarService.addYears(this.configurationInfo.endDate, -1)

    const minDate = endDate === null || endDate < now ? now : endDate

    return dateService.stringifyDate(minDate)
  }

  public get maxDate(): string | null {
    if (this.configurationInfo.startDate === null) {
      return null
    }

    return dateService.stringifyDate(calendarService.addYears(this.configurationInfo.startDate, 1))
  }

  public get startDate(): string | null {
    if (this.configurationInfo.startDate === null) {
      return null
    }

    return dateService.stringifyDate(this.configurationInfo.startDate)
  }

  public get endDate(): string | null {
    if (this.configurationInfo.endDate === null) {
      return null
    }

    return dateService.stringifyDate(this.configurationInfo.endDate)
  }

  public get selectedPeriod(): number | null {
    if (this.configurationInfo.startDate !== null && this.configurationInfo.endDate !== null) {
      return dateService.diffDate(this.configurationInfo.startDate, this.configurationInfo.endDate) + 1
    }

    return null
  }

  public get weeks(): number {
    if (this.configurationInfo.startDate !== null && this.configurationInfo.endDate !== null) {
      return calendarService.getStartedWeeks(this.configurationInfo.startDate, this.configurationInfo.endDate)
    }

    return 0
  }

  protected get minEndDate(): string {
    if (this.configurationInfo.startDate !== null) {
      return dateService.stringifyDate(calendarService.addDays(this.configurationInfo.startDate, 1))
    }
    return this.minDate
  }
  //#endregion

  //#region [Mutation]
  @Mutation('STORE', { namespace: 'temporaryConnection/configurationInfo' })
  private store!: (index: TemporaryConfigurationInfo | null) => void
  //#endregion

  //#region [Method]
  public mounted() {
    this.setIsBack(false)
    if (this.configurationInfoState !== null) {
      this.configurationInfo = helper.clone(this.configurationInfoState)
    }
  }

  public async save(next: boolean, isValid: boolean) {
    if (isValid) {
      this.$appInsights.trackEvent('WR_TC_StepConfigurationInfo_Validated', {
        Segment: 'WR_TC',
        WebRef: this.requestId
      })
    }

    this.store(this.configurationInfo)
  }
  //#endregion
}
</script>